/**
 * Hanging Protocol Settings Component
 * Gestionează mapările Hanging Protocol pentru OHIF Viewer
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Paper,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GridView as GridViewIcon,
} from '@mui/icons-material'
import { toast } from 'react-hot-toast'
import api from '../../services/api'

// Tipuri
interface HangingProtocolMapping {
  id: string
  tenant_id: string
  modality: string | null
  body_part: string | null
  hanging_protocol_id: string
  name: string
  description: string | null
  priority: number
  is_active: boolean
  created_at: string
  updated_at: string
}

interface AvailableProtocol {
  id: string
  name: string
  description: string
}

interface FormData {
  modality: string
  body_part: string
  hanging_protocol_id: string
  name: string
  description: string
  priority: number
  is_active: boolean
}

const MODALITIES = [
  { value: '', label: 'Orice modalitate' },
  { value: 'CT', label: 'CT - Tomografie Computerizată' },
  { value: 'MR', label: 'MR - Rezonanță Magnetică' },
  { value: 'MG', label: 'MG - Mamografie' },
  { value: 'DX', label: 'DX - Radiografie Digitală' },
  { value: 'CR', label: 'CR - Radiografie Convențională' },
  { value: 'US', label: 'US - Ecografie' },
  { value: 'PT', label: 'PT - PET' },
  { value: 'NM', label: 'NM - Medicină Nucleară' },
  { value: 'XA', label: 'XA - Angiografie' },
  { value: 'RF', label: 'RF - Fluoroscopie' },
]

const BODY_PARTS = [
  { value: '', label: 'Orice zonă anatomică' },
  { value: 'HEAD', label: 'Cap' },
  { value: 'BRAIN', label: 'Creier' },
  { value: 'NECK', label: 'Gât' },
  { value: 'CHEST', label: 'Torace' },
  { value: 'THORAX', label: 'Torace (Thorax)' },
  { value: 'ABDOMEN', label: 'Abdomen' },
  { value: 'PELVIS', label: 'Pelvis' },
  { value: 'SPINE', label: 'Coloană' },
  { value: 'CSPINE', label: 'Coloană Cervicală' },
  { value: 'TSPINE', label: 'Coloană Toracică' },
  { value: 'LSPINE', label: 'Coloană Lombară' },
  { value: 'BREAST', label: 'Sân' },
  { value: 'EXTREMITY', label: 'Extremități' },
  { value: 'HAND', label: 'Mână' },
  { value: 'FOOT', label: 'Picior' },
  { value: 'KNEE', label: 'Genunchi' },
  { value: 'HIP', label: 'Șold' },
  { value: 'SHOULDER', label: 'Umăr' },
]

const initialFormData: FormData = {
  modality: '',
  body_part: '',
  hanging_protocol_id: '@ohif/mnGrid',
  name: '',
  description: '',
  priority: 0,
  is_active: true,
}

const HangingProtocolSettings: React.FC = () => {
  const [mappings, setMappings] = useState<HangingProtocolMapping[]>([])
  const [availableProtocols, setAvailableProtocols] = useState<AvailableProtocol[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [saving, setSaving] = useState(false)

  // Încarcă mapările și protocoalele disponibile
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [mappingsRes, protocolsRes] = await Promise.all([
        api.get('/admin/hanging-protocols'),
        api.get('/admin/hanging-protocols/available'),
      ])
      setMappings(mappingsRes.data)
      setAvailableProtocols(protocolsRes.data.protocols)
    } catch (error) {
      console.error('Error loading HP data:', error)
      toast.error('Eroare la încărcarea configurărilor HP')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Deschide dialog pentru creare
  const handleCreate = () => {
    setEditingId(null)
    setFormData(initialFormData)
    setDialogOpen(true)
  }

  // Deschide dialog pentru editare
  const handleEdit = (mapping: HangingProtocolMapping) => {
    setEditingId(mapping.id)
    setFormData({
      modality: mapping.modality || '',
      body_part: mapping.body_part || '',
      hanging_protocol_id: mapping.hanging_protocol_id,
      name: mapping.name,
      description: mapping.description || '',
      priority: mapping.priority,
      is_active: mapping.is_active,
    })
    setDialogOpen(true)
  }

  // Confirmă ștergerea
  const handleDeleteClick = (id: string) => {
    setDeletingId(id)
    setDeleteDialogOpen(true)
  }

  // Salvează maparea
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Numele este obligatoriu')
      return
    }
    if (!formData.hanging_protocol_id) {
      toast.error('Protocolul este obligatoriu')
      return
    }

    try {
      setSaving(true)
      const payload = {
        ...formData,
        modality: formData.modality || null,
        body_part: formData.body_part || null,
      }

      if (editingId) {
        await api.put(`/admin/hanging-protocols/${editingId}`, payload)
        toast.success('Mapare actualizată')
      } else {
        await api.post('/admin/hanging-protocols', payload)
        toast.success('Mapare creată')
      }

      setDialogOpen(false)
      loadData()
    } catch (error: any) {
      console.error('Error saving HP mapping:', error)
      const message = error.response?.data?.detail || 'Eroare la salvare'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  // Șterge maparea
  const handleDelete = async () => {
    if (!deletingId) return

    try {
      await api.delete(`/admin/hanging-protocols/${deletingId}`)
      toast.success('Mapare ștearsă')
      setDeleteDialogOpen(false)
      setDeletingId(null)
      loadData()
    } catch (error) {
      console.error('Error deleting HP mapping:', error)
      toast.error('Eroare la ștergere')
    }
  }

  // Găsește numele protocolului
  const getProtocolName = (id: string) => {
    const protocol = availableProtocols.find(p => p.id === id)
    return protocol?.name || id
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Compact styles
  const tableSx = { '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' }, '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 }, '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' } };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Configurați layout-ul OHIF Viewer bazat pe tipul de studiu</Typography>
        <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={handleCreate} size="small" sx={{ height: 26, fontSize: '0.72rem', textTransform: 'none' }}>Adaugă Mapare</Button>
      </Box>

      {mappings.length === 0 ? (
        <Alert severity="info" sx={{ mb: 1, py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>Nu există mapări. Se va folosi layout-ul implicit (Grid 2x2).</Alert>
      ) : (
        <Paper variant="outlined" sx={tableSx}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.72rem', fontWeight: 600, py: 0.5, px: 1 } }}>
                <TableCell>Nume</TableCell>
                <TableCell>Mod.</TableCell>
                <TableCell>Zonă</TableCell>
                <TableCell>Protocol</TableCell>
                <TableCell align="center">Pri.</TableCell>
                <TableCell align="center">Activ</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.map((mapping) => (
                <TableRow key={mapping.id} hover>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{mapping.name}</Typography>
                    {mapping.description && <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{mapping.description}</Typography>}
                  </TableCell>
                  <TableCell><Chip label={mapping.modality || '*'} size="small" color={mapping.modality ? 'primary' : 'default'} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} /></TableCell>
                  <TableCell><Chip label={mapping.body_part || '*'} size="small" color={mapping.body_part ? 'secondary' : 'default'} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} /></TableCell>
                  <TableCell><Tooltip title={mapping.hanging_protocol_id}><Chip icon={<GridViewIcon sx={{ fontSize: 12 }} />} label={getProtocolName(mapping.hanging_protocol_id).substring(0, 15)} size="small" color="info" sx={{ height: 20, fontSize: '0.65rem' }} /></Tooltip></TableCell>
                  <TableCell align="center"><Typography sx={{ fontSize: '0.75rem' }}>{mapping.priority}</Typography></TableCell>
                  <TableCell align="center"><Chip label={mapping.is_active ? 'Da' : 'Nu'} size="small" color={mapping.is_active ? 'success' : 'default'} sx={{ height: 18, fontSize: '0.65rem' }} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => handleEdit(mapping)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Șterge"><IconButton size="small" color="error" onClick={() => handleDeleteClick(mapping.id)}><DeleteIcon /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Dialog Creare/Editare - Compact */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { border: '1px solid', borderColor: 'divider' } }}>
        <DialogTitle sx={{ py: 1.5, fontSize: '0.95rem', fontWeight: 600 }}>{editingId ? 'Editează Mapare HP' : 'Adaugă Mapare HP'}</DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
            <TextField label="Nume *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth size="small" placeholder="Ex: CT Torace" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }} />
            <TextField label="Descriere" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth size="small" multiline rows={1} sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }} />
            <FormControl fullWidth size="small" sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel>Modalitate</InputLabel>
              <Select value={formData.modality} onChange={(e) => setFormData({ ...formData, modality: e.target.value })} label="Modalitate" sx={{ fontSize: '0.8rem' }}>{MODALITIES.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: '0.8rem' }}>{m.label}</MenuItem>)}</Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel>Zonă Anatomică</InputLabel>
              <Select value={formData.body_part} onChange={(e) => setFormData({ ...formData, body_part: e.target.value })} label="Zonă Anatomică" sx={{ fontSize: '0.8rem' }}>{BODY_PARTS.map((bp) => <MenuItem key={bp.value} value={bp.value} sx={{ fontSize: '0.8rem' }}>{bp.label}</MenuItem>)}</Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel>Protocol OHIF *</InputLabel>
              <Select value={formData.hanging_protocol_id} onChange={(e) => setFormData({ ...formData, hanging_protocol_id: e.target.value })} label="Protocol OHIF *" sx={{ fontSize: '0.8rem' }}>{availableProtocols.map((p) => <MenuItem key={p.id} value={p.id} sx={{ fontSize: '0.8rem' }}>{p.name}</MenuItem>)}</Select>
            </FormControl>
            <TextField label="Prioritate" type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })} fullWidth size="small" helperText="Mai mare = prioritate mai mare" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiFormHelperText-root': { fontSize: '0.68rem' } }} />
            <FormControlLabel control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} size="small" />} label={<Typography sx={{ fontSize: '0.8rem' }}>Activ</Typography>} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 1.5, px: 2 }}>
          <Button onClick={() => setDialogOpen(false)} size="small" sx={{ fontSize: '0.75rem', textTransform: 'none' }}>Anulează</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} size="small" sx={{ fontSize: '0.75rem', textTransform: 'none' }}>{saving ? <CircularProgress size={16} /> : 'Salvează'}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Confirmare Ștergere - Compact */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { border: '1px solid', borderColor: 'divider' } }}>
        <DialogTitle sx={{ py: 1.5, fontSize: '0.95rem', fontWeight: 600 }}>Confirmare Ștergere</DialogTitle>
        <DialogContent sx={{ py: 1 }}><Typography sx={{ fontSize: '0.85rem' }}>Sigur doriți să ștergeți această mapare?</Typography></DialogContent>
        <DialogActions sx={{ py: 1.5, px: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ fontSize: '0.75rem', textTransform: 'none' }}>Anulează</Button>
          <Button onClick={handleDelete} color="error" variant="contained" size="small" sx={{ fontSize: '0.75rem', textTransform: 'none' }}>Șterge</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default HangingProtocolSettings
